import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  Mail,
  ChevronDown,
  ChevronUp,
  HelpCircle,
  MessageSquare,
  Loader2,
} from 'lucide-react';
import { useGetLegalPageQuery } from '@nimbazaar/store';

interface FaqItem {
  question: string;
  answer: string;
}

interface SupportContent {
  heading: string;
  description: string;
  email: string;
  responseTime: string;
}

const DEFAULT_SUPPORT: SupportContent = {
  heading: 'Get in Touch',
  description: 'Have a question, suggestion, or need help? Send us your queries and we\'ll get back to you as soon as possible.',
  email: 'nimbazaar7@gmail.com',
  responseTime: 'We typically respond within 24 hours',
};

const DEFAULT_FAQS: FaqItem[] = [
  { question: 'What is NimBazaar?', answer: 'NimBazaar is a "Wanted Ads" marketplace where buyers post ads for products or services they need, and sellers respond with competitive bids.' },
  { question: 'How do credits work?', answer: 'You receive free credits when you sign up. Credits are used to post ads. You can earn more credits through referrals or by watching ads.' },
  { question: 'How do I post an ad?', answer: 'Click "Post Ad", fill in details about what you\'re looking for, select a category, set your budget range, and submit.' },
  { question: 'How do I bid on an ad?', answer: 'Browse ads, click on one you can fulfill, and submit your bid with your price and description.' },
  { question: 'Is NimBazaar free to use?', answer: 'Yes! Signing up is free and you get bonus credits to start. Posting ads costs a small number of credits.' },
];

export function Support() {
  const { data: supportData, isLoading: supportLoading } = useGetLegalPageQuery('support');
  const { data: faqData, isLoading: faqLoading } = useGetLegalPageQuery('faq');
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const support: SupportContent = (() => {
    const content = supportData?.data?.content;
    if (content) {
      try { return { ...DEFAULT_SUPPORT, ...JSON.parse(content) }; } catch { return DEFAULT_SUPPORT; }
    }
    return DEFAULT_SUPPORT;
  })();

  const faqs: FaqItem[] = (() => {
    const content = faqData?.data?.content;
    if (content) {
      try { return JSON.parse(content); } catch { return DEFAULT_FAQS; }
    }
    return DEFAULT_FAQS;
  })();

  useEffect(() => {
    document.title = 'Contact Us / Support - NimBazaar';
  }, []);

  const isLoading = supportLoading || faqLoading;

  return (
    <div className="min-h-screen bg-gradient-to-b from-primary-50/50 to-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary-800 to-primary-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-primary-200 hover:text-white mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-white/10 backdrop-blur flex items-center justify-center">
              <MessageSquare className="w-7 h-7 text-secondary-400" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold">Contact Us / Support</h1>
              <p className="text-sm text-primary-200 mt-1">We're here to help you</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12 space-y-10">
        {isLoading ? (
          <div className="flex items-center justify-center py-20 text-gray-400">
            <Loader2 className="w-6 h-6 animate-spin mr-2" />
            Loading...
          </div>
        ) : (
          <>
            {/* Contact Section */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-10">
              <h2 className="text-xl font-bold text-gray-900 mb-2">{support.heading}</h2>
              <p className="text-gray-500 mb-6">{support.description}</p>

              <div className="bg-gradient-to-r from-primary-50 to-blue-50 rounded-xl p-6 border border-primary-100">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-primary-100 rounded-2xl flex items-center justify-center shrink-0">
                    <Mail className="w-7 h-7 text-primary-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Email us at</p>
                    <a
                      href={`mailto:${support.email}`}
                      className="text-xl font-bold text-primary-600 hover:text-primary-700 transition-colors"
                    >
                      {support.email}
                    </a>
                    <p className="text-sm text-gray-400 mt-1">{support.responseTime}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* FAQ Section */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-10">
              <div className="flex items-center gap-3 mb-6">
                <HelpCircle className="w-6 h-6 text-primary-600" />
                <h2 className="text-xl font-bold text-gray-900">Frequently Asked Questions</h2>
              </div>

              <div className="space-y-3">
                {faqs.map((faq, index) => (
                  <div
                    key={index}
                    className={`rounded-xl border transition-colors ${
                      openIndex === index ? 'border-primary-200 bg-primary-50/30' : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <button
                      onClick={() => setOpenIndex(openIndex === index ? null : index)}
                      className="w-full flex items-center justify-between p-4 text-left"
                    >
                      <span className="font-medium text-gray-900 pr-4">{faq.question}</span>
                      {openIndex === index ? (
                        <ChevronUp className="w-5 h-5 text-primary-500 shrink-0" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-400 shrink-0" />
                      )}
                    </button>
                    {openIndex === index && (
                      <div className="px-4 pb-4">
                        <p className="text-gray-600 text-sm leading-relaxed">{faq.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
